/**
 * Pure derivations for the Finanzen tab. No React, no fetching, no clock.
 *
 * WHAT IS NEW HERE AND WHAT IS BORROWED
 * -------------------------------------
 * The five period figures are the SAME quantities the company overview already
 * states, read from the SAME contracts, so their selectors are imported rather
 * than rewritten. A second `expected - costs` in this folder would be a second
 * definition of the contribution margin, and the two would drift the first time
 * one of them was "fixed".
 *
 * The period resolution is borrowed for the same reason (P3c). Finanzen used to
 * own a finance-specific gate — `selectFinanceMonth`, which accepted one month
 * and refused everything else. It now reads the SAME four contracts the overview
 * reads, with the SAME four wire parameters, so a second resolver here would be a
 * second answer to "which period is selected" on one page.
 */
import {
  overviewWindowKey,
  selectOverviewWindow,
  windowLabel,
} from "../companyOverview/overviewSelectors";
import {
  selectCashIn,
  selectContributionMargin,
  selectExpectedContribution,
  selectExpectedRevenue,
  selectInvoiced,
  selectOpenReceivables,
  selectOperatingCosts,
  selectTenantCashDeposits,
  selectUnclassifiedCosts,
  toNumber,
} from "../companyOverview/overviewSelectors";
import {
  AGING_BUCKET_LABEL,
  AGING_BUCKET_ORDER,
} from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";
// F2c: the cost-structure card moved to this folder; its selector stayed where
// it is. `overviewChartSelectors` still owns the revenue trend the Übersicht
// draws, and splitting one module in two so each tab can own "its" half would
// leave two files describing the same A2 contract. Borrowed and re-exported
// below, exactly like the five period selectors above.
import { selectCostStructure } from "../companyOverview/overviewChartSelectors";
// The one unit-identity contract in the app: short code on top, full postal
// address underneath. Twenty other surfaces already read it; a ranking that
// spelled its own would be the twenty-first opinion.
import {
  getUnitAddressSubtitle,
  getUnitHeadingLabel,
} from "../../../utils/unitDisplayId";

// Named re-exports, never `export *`: grep for a symbol must find one place.
// `selectOpenReceivables` and `selectTenantCashDeposits` keep the names they
// already have rather than gaining finance-local aliases — one function under
// two names is the same drift risk as two functions under one.
export {
  selectCashIn,
  selectContributionMargin,
  selectCostStructure,
  selectExpectedContribution,
  selectExpectedRevenue,
  selectInvoiced,
  selectOpenReceivables,
  selectOperatingCosts,
  selectTenantCashDeposits,
  selectUnclassifiedCosts,
  // P3c. Re-exported under their own names, not finance-local aliases: the
  // window a Finanzen card labels and the window the Übersicht requests must be
  // provably the same object shape, and one function under two names is the
  // drift risk this file exists to avoid.
  overviewWindowKey,
  selectOverviewWindow,
  windowLabel,
};

/**
 * The four wire parameters every period-aware finance contract takes.
 *
 * `revenue-analytics`, `bexio/company-invoiced`, `dashboard/cost-breakdown` and
 * `dashboard/cost-ranking` all read `year`/`month` plus an optional
 * `to_year`/`to_month` (P3a), so one resolved window is spread straight into
 * each of them and no call site re-derives a calendar.
 *
 * `monthCount` and `isRange` are derived facts for the UI, not parameters —
 * sending them would be inventing fields the endpoints do not accept.
 */
export function financeRequestParams(window) {
  if (!window) return null;
  return {
    year: window.year,
    month: window.month,
    to_year: window.to_year,
    to_month: window.to_month,
  };
}

/**
 * The unclassified remainder, reported BESIDE the operating total and never
 * inside it.
 *
 * A2 classifies fail-closed: a cost row whose `cost_type` it does not recognise
 * is counted here instead of being folded into a bucket. That is the whole
 * point — production carries a free-text "Kaution" cost row, and the legacy
 * `summary.total_costs` silently reported it as an operating expense. So this
 * returns its own object; adding it to `operating_costs.total_chf` anywhere
 * would re-create exactly the figure F2a replaces.
 *
 * `present` is false at zero: a portfolio whose every cost row classifies has
 * nothing wrong with it, and a line saying "CHF 0 unclassified" is noise.
 */
export function selectUnclassifiedNotice(costBreakdown) {
  const total = selectUnclassifiedCosts(costBreakdown);
  const count = costBreakdown?.unclassified?.cost_row_count;
  return {
    total,
    // The untouched contract value, so the hint can print the exact amount
    // rather than a franc-rounded one — the cents are the point on a figure
    // this small.
    raw: costBreakdown?.unclassified?.total_monthly_equivalent_chf,
    count: Number.isFinite(Number(count)) ? Number(count) : 0,
    present: total !== null && total > 0,
  };
}

/* ─────────────────────────────────────────────────────────────────────────
 * CURRENT STATE (F2b)
 *
 * Everything below answers "right now", not "in the selected month". The
 * receivables block travels inside the SAME `revenue-analytics` response the
 * period figures come from — `current_receivables` is org-wide and across all
 * rent months, and the backend states so on the wire
 * (`scope.receivables_scope === "current_all_periods"`). It is refreshed as a
 * side effect of a month change purely because the request it rides in is; the
 * VALUE does not depend on the month, and no separate request is made for it.
 *
 * NOTHING HERE COMPUTES A DUE DATE. Not one comparison against today, not one
 * day-difference, not one `status === "overdue"` filter over an invoice list.
 * The backend owns the ageing rule and publishes both the roll-ups and the
 * buckets; re-deriving either in a browser would mean a second definition of
 * "overdue" running on the device clock.
 * ───────────────────────────────────────────────────────────────────────── */

/** The day the receivables answer was computed, as the contract's own `YYYY-MM-DD`. */
export function selectReceivablesAsOf(revenueAnalytics) {
  return revenueAnalytics?.current_receivables?.as_of_date ?? null;
}

/** How many invoices make up `open_current`. `null` when the block is absent. */
export function selectOpenInvoiceCount(revenueAnalytics) {
  return toNumber(revenueAnalytics?.current_receivables?.open_invoice_count);
}

/**
 * Everything whose due date has ARRIVED — today's included.
 *
 * `due_current` is the backend roll-up, and it is deliberately NOT "overdue".
 * `bexio_aging_service.py:128` defines `_DUE_BUCKETS` as `due_today` plus every
 * `overdue_*` bucket and `:364` sums exactly those, which reproduces the
 * repository's existing `due_date <= as_of` rule. An invoice due today has an
 * age of zero days: it IS due, and it is NOT overdue.
 *
 * So the figure is used as published and the label says "Fällig & überfällig".
 * Narrowing it to the `overdue_*` buckets alone would be a different question,
 * and answering it by subtracting `due_today` here would put the ageing rule
 * back in the browser — which is the one thing this selector exists to avoid.
 */
export function selectDueOrOverdue(revenueAnalytics) {
  return toNumber(revenueAnalytics?.current_receivables?.due_current);
}

/**
 * The seven ageing buckets as display rows, in the contract's own order.
 *
 * THE ORDER AND THE LABELS ARE BORROWED, NOT RESTATED. `AGING_BUCKET_ORDER` and
 * `AGING_BUCKET_LABEL` already exist for the revenue-analytics page and match
 * `bexio_aging_service.BUCKET_KEYS` one for one. A second copy here would be a
 * second thing to keep in step with the backend.
 *
 * A bucket the payload does not carry yields `amount: null`, which renders as a
 * dash. It is NOT turned into a zero: "the backend did not tell us" and "the
 * backend said none" are different claims, and only one of them is safe to make
 * on a receivables screen. An EXPLICIT zero stays a zero.
 *
 * `share` is display geometry only — each amount against the largest one, for a
 * bar width. It is not a percentage of the receivables total and is never shown
 * as a figure; the row always carries the amount itself as text.
 */
export function selectAgingRows(revenueAnalytics) {
  const buckets = revenueAnalytics?.current_receivables?.buckets;
  if (!Array.isArray(buckets)) return [];

  const rows = AGING_BUCKET_ORDER.map((key) => {
    const bucket = buckets.find((b) => b && b.key === key);
    const amount = toNumber(bucket?.amount);
    return {
      key,
      label: AGING_BUCKET_LABEL[key],
      amount,
      count: toNumber(bucket?.invoice_count),
      // Whether the contract carried this bucket at all — the difference
      // between "—" and a real "CHF 0.00".
      present: bucket !== undefined,
    };
  });

  // Largest bucket, for the bar scale. `Math.max` over the known amounts, with
  // a floor of 0 so an all-null or all-zero set cannot divide by zero.
  const amounts = rows.map((r) => (r.amount === null ? 0 : r.amount));
  const max = Math.max(0, ...amounts);

  return rows.map((row) => ({
    ...row,
    share: max > 0 && row.amount !== null && row.amount > 0 ? row.amount / max : 0,
  }));
}

/**
 * Invoices the ageing population left out for a reason that could hide a real
 * receivable — a COUNT notice, never an amount added back.
 *
 * The contract's `excluded` carries seven counters and they do not mean the same
 * thing. `draft` is not yet a claim, `canceled` is withdrawn and `settled` has
 * nothing left to owe: those three are correctly absent and reporting them would
 * put a permanent warning on a healthy portfolio. The four below are the ones an
 * operator cannot explain away — `provider_missing` is a row a completed
 * provider inventory no longer contains, `unknown` could not be classified
 * without guessing, and the two currency counters hold real amounts that cannot
 * be summed into a franc total because no exchange-rate source exists here
 * (`backend/app/schemas/bexio_aging.py`, `AgingExcludedCounts`).
 *
 * Their AMOUNTS are deliberately not fetched, not summed and not added to
 * `open_current`. Adding them back would re-introduce exactly the guess the
 * backend refused to make.
 */
const RECEIVABLES_EXCLUDED_UNEXPLAINED = [
  "provider_missing",
  "unknown",
  "non_chf",
  "unknown_currency",
];

export function selectReceivablesExcludedNotice(revenueAnalytics) {
  const excluded = revenueAnalytics?.current_receivables?.excluded;
  if (!excluded) return { count: 0, present: false };

  // A plain loop over four counters. Counts of invoices, never money.
  let count = 0;
  for (const key of RECEIVABLES_EXCLUDED_UNEXPLAINED) {
    const n = toNumber(excluded[key]);
    if (n !== null && n > 0) count += n;
  }
  return { count, present: count > 0 };
}

/**
 * Capital currently placed WITH landlords, from `/deposits/landlord-placed`.
 *
 * The opposite side of the balance from `selectTenantCashDeposits`, which is
 * money tenants placed with US. They share the German word "Kaution" and
 * nothing else: one is bound capital, the other a liability. They are never
 * summed, and neither is a cost or a revenue.
 */
export function selectLandlordDeposits(landlordPlaced) {
  return toNumber(landlordPlaced?.total_chf);
}

/** The day the landlord deposit answer was computed. Its own date, not the receivables one. */
export function selectLandlordDepositsAsOf(landlordPlaced) {
  return landlordPlaced?.as_of ?? null;
}

/**
 * The ANNUAL guarantee premium across insured deposits — context, never a cost.
 *
 * `annual_premium_total_chf` is a year figure. Its monthly, lease-prorated form
 * is already inside `operating_costs.total_chf` as `deposit_insurance_chf`
 * (A2 computes it there so one period rule serves every cost figure), so adding
 * this to any operating total would count the same arrangement twice and on the
 * wrong time axis at that. It is presented with "p.a." attached for that reason.
 *
 * `present` is false at zero: a portfolio with no insured deposits has no
 * premium to state, and "CHF 0.00 p.a." is noise rather than information.
 */
export function selectLandlordPremiumContext(landlordPlaced) {
  const total = toNumber(landlordPlaced?.annual_premium_total_chf);
  return { total, present: total !== null && total > 0 };
}

/**
 * Insured deposits whose annual premium nobody recorded.
 *
 * The contract calls this out as the one data-quality finding with a cost
 * consequence: such a unit contributes nothing to `deposit_insurance_chf`, so
 * the expense is MISSING rather than zero. The notice says how many; it does not
 * invent a premium for them, and nothing downstream treats the gap as CHF 0.
 */
export function selectLandlordDepositQuality(landlordPlaced) {
  const count = toNumber(
    landlordPlaced?.data_quality?.insurance_units_without_annual_premium
  );
  return { count, present: count !== null && count > 0 };
}

/**
 * How the securities placed with landlords are actually provided.
 *
 * WHY THIS EXISTS. `total_chf` alone was published under the title "Gebundenes
 * Kapital", which claims the money is tied up. For a deposit of type
 * `insurance` that is not what the figure is: `unit.landlord_deposit_amount`
 * holds the GUARANTEED sum, and what actually leaves our account is the annual
 * premium. Production carries five insured units whose amounts add to the whole
 * headline — so under the old title 100 % of it read as blocked liquidity that
 * is not blocked at all. The breakdown is what makes the headline honest, and
 * the title now says "Sicherheitsleistungen": securities provided, whatever
 * form they take.
 *
 * ORDER is fixed and does not follow the payload: insurance first because it is
 * the form that most needs distinguishing from cash.
 *
 * ROWS DO NOT NECESSARILY SUM TO THE HEADLINE, and none is invented to make
 * them. `total_chf` counts every positive amount whatever the type says
 * (`dashboard_deposit_service.py:120`), while `by_type` only receives amounts
 * whose type is one of the four known ones — a unit with an unrecognised or
 * empty type contributes to the total and to no bucket. The contract reports
 * those separately as `unknown_deposit_type_units` /
 * `positive_amount_without_type`; a synthetic "Rest" row here would paper over
 * exactly that finding.
 *
 * `share` is a PERCENTAGE (0..100), null when there is no base to divide by.
 * Never 0/0, never Infinity, never a fabricated quota.
 */
export const LANDLORD_SECURITY_TYPE_ORDER = ["insurance", "bank", "cash", "none"];

/**
 * German labels, matching the ones the unit detail page and the audit display
 * already use for `landlord_deposit_type`.
 *
 * Deliberately a local copy: both existing maps
 * (`pages/admin/AdminUnitDetailPage.js:127`, `utils/auditDisplay.js:26`) are
 * plain `const` with no export, so there is nothing importable to reuse.
 * Exporting one of them and collapsing all three is a cleanup of its own —
 * see the K1 report — and doing it here would widen a deliberately small slice
 * into two unrelated files.
 */
export const LANDLORD_SECURITY_TYPE_LABEL = {
  insurance: "Versicherung",
  bank: "Bankdepot",
  cash: "Bareinzahlung",
  none: "Keine",
};

export function selectLandlordSecurityTypeRows(landlordPlaced) {
  const byType = landlordPlaced?.by_type;
  if (!byType) return [];

  const total = toNumber(landlordPlaced?.total_chf);
  // A base to divide by, or no percentages at all. `total_chf` sums only
  // positive amounts, so a zero total means every bucket is zero too and the
  // filter below empties the list — but an inconsistent payload must still not
  // produce NaN or Infinity here.
  const hasBase = total !== null && total > 0;

  return LANDLORD_SECURITY_TYPE_ORDER.map((type) => {
    const amount = toNumber(byType[type]?.total_chf);
    return {
      type,
      label: LANDLORD_SECURITY_TYPE_LABEL[type],
      amount,
      unitCount: toNumber(byType[type]?.unit_count),
      share: hasBase && amount !== null && amount > 0 ? (amount / total) * 100 : null,
    };
  }).filter((row) => row.amount !== null && row.amount > 0);
}

/* ─────────────────────────────────────────────────────────────────────────
 * F2c — Zahlungseingänge (payment receipts) and the cost ranking.
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * How many months the receipts chart shows under a SINGLE-month selection.
 *
 * Six, because the backend's expected-revenue history is six
 * (`HISTORY_MONTHS`, `expected_revenue_analytics_calculations.py:53`) and two
 * charts on one surface reading different window lengths would invite a
 * comparison that is not valid.
 *
 * P3c: under a RANGE this constant does not apply at all. The chart is then the
 * selection itself — three months selected, three buckets; twelve, twelve — and
 * a fixed six would either hide months the heading names or show months it does
 * not.
 */
export const CASHFLOW_TREND_MONTHS = 6;

/** A month as a sortable integer, so a window test needs no date arithmetic. */
const monthOrdinal = (year, month) => year * 12 + (month - 1);

/**
 * The receipts that fall inside the selected window.
 *
 * WHICH MONTHS (P3c). A range shows EXACTLY the selected months; a single month
 * keeps its six-month run-up, because one bar is not a series. The run-up is the
 * only case where this function names a span the user did not select, and it is
 * the behaviour the card has always had.
 *
 * WHY THE FRONTEND SELECTS AT ALL. `cashflow_trend` is `cashflow["monthly"]`,
 * the FULL observed history — P3a deliberately left it unclipped so consumers
 * can take the span they need, and clipping it server-side would have shortened
 * a chart that asked for more. Selecting which of the backend's own monthly
 * buckets fall inside the window is not a recomputation: no figure is summed,
 * scaled or re-bucketed here, and each row keeps the contract's own value.
 *
 * FILTER, NEVER FILL. The series is SPARSE by contract: it holds only months
 * with at least one TRUSTED payment, and an absent month means "we cannot vouch
 * for anything here", not "CHF 0" (`bexio_cashflow_service.py:412-414`). So this
 * selects the rows that fall in the window and returns them in order — it never
 * materialises a month the backend did not send. A zero-filled gap would be a
 * claim about money, made by the browser, on an axis whose completeness the
 * backend explicitly refuses to assert (`history_completeness_verified: false`).
 *
 * That is also why the window is applied by FILTERING rather than by walking the
 * slots and looking each one up: the walk produces a row per slot, and the only
 * way to render it is to invent a value for the misses.
 *
 * A FUTURE selected month needs no special case. The window still ends there,
 * and months after the last real payment simply have no rows to select — so
 * nothing is drawn for them rather than a zero or an extrapolation.
 */
export function selectCashflowWindow(cashflowTrend, window) {
  if (!window || !Array.isArray(cashflowTrend)) return [];
  const end = monthOrdinal(window.to_year, window.to_month);
  // A range starts where it says it starts. A single month reaches back for its
  // run-up — `isRange` is the server-independent fact the resolver already
  // published, so the branch needs no second comparison of the two month pairs.
  const start = window.isRange
    ? monthOrdinal(window.year, window.month)
    : end - (CASHFLOW_TREND_MONTHS - 1);

  return cashflowTrend
    .filter((row) => {
      const y = Number(row?.year);
      const m = Number(row?.month);
      if (!Number.isFinite(y) || !Number.isFinite(m)) return false;
      const ord = monthOrdinal(y, m);
      return ord >= start && ord <= end;
    })
    .map((row) => ({
      year: Number(row.year),
      month: Number(row.month),
      // The contract value, untouched, for the tooltip and the text summary.
      raw: row?.received,
      received: toNumber(row?.received),
      paymentCount: toNumber(row?.payment_count) ?? 0,
    }))
    .sort((a, b) => monthOrdinal(a.year, a.month) - monthOrdinal(b.year, b.month));
}

/**
 * The ranked units, in the order the contract returned them.
 *
 * NO RE-SORT AND NO FILTER. `/dashboard/cost-ranking` orders on the OPERATING
 * total with `short_unit_id` then `unit_id` as tie-breaks
 * (`routes_admin_dashboard_costs.py:102-110`), and it already decided which
 * units belong: a unit whose lease overlaps the month is in it even at CHF 0,
 * and one whose lease has not begun is absent. A client-side `cost > 0` filter
 * would silently drop a real answer, and a client-side sort would rank on
 * whatever field happened to be handy — including `unclassified_total_chf`,
 * which the contract deliberately keeps OUT of the order.
 *
 * The label falls back rather than inventing one: `title`, then the short id,
 * then the raw id. A unit with no title is a data gap, not a nameless unit.
 */
export function selectCostRankingRows(costRanking) {
  const items = costRanking?.items;
  if (!Array.isArray(items)) return [];

  return items.map((row, index) => ({
    rank: index + 1,
    unitId: row?.unit_id ?? null,
    label: rankingUnitLabel(row),
    location: rankingUnitLocation(row),
    amount: toNumber(row?.operating_cost_total_chf),
    raw: row?.operating_cost_total_chf,
  }));
}

/**
 * The ranking row in the shape `unitDisplayId` reads.
 *
 * `getUnitAddressSubtitle` treats `address_line1`/`street` as the STREET and
 * `address` as an already-complete address. The cost-ranking contract carries
 * the street in `address` (`dashboard_costs.py` CostRankingItem), so it is
 * mapped onto `street` — handing it over as `address` would make the helper
 * return the street alone and silently drop the postal code and city.
 */
function toUnitShape(row) {
  return {
    id: row?.unit_id ?? null,
    unitId: row?.unit_id ?? null,
    shortUnitId: row?.short_unit_id ?? null,
    short_unit_id: row?.short_unit_id ?? null,
    title: row?.title ?? null,
    street: row?.address ?? null,
    postal_code: row?.postal_code ?? null,
    city: row?.city ?? null,
  };
}

/**
 * Primary line: the short code first.
 *
 * It used to be `title` first, and that is the bug this replaces. `title` is
 * free text; an organisation that names its units after their location gets a
 * ranking reading "Zürich, Zürich, Zürich" — three rows that name no unit at
 * all. `short_unit_id` is NOT NULL and unique per organisation (migration 068),
 * so it identifies every row without a tie-breaker. `title` stays as the
 * fallback for a row that somehow has no code, and the unit id after that,
 * because a wrong-looking label still beats an empty one.
 */
function rankingUnitLabel(row) {
  const code = String(row?.short_unit_id ?? "").trim();
  if (code) return code;
  const title = String(row?.title ?? "").trim();
  if (title) return title;
  // Last resort only. `getUnitHeadingLabel` cannot own the middle rung: it goes
  // from the code straight to the unit id and never returns a title, so asking
  // it first would make the title branch above unreachable. It still owns this
  // rung, which is the one with a shape question (`unitId` vs `id`) in it.
  const heading = getUnitHeadingLabel(toUnitShape(row));
  return heading && heading !== "\u2014" ? heading : null;
}

/**
 * Secondary line: street, postal code and city — never the city alone.
 *
 * The city alone was the other half of the bug: under a title that is also the
 * city, it printed the same word twice and disambiguated nothing.
 *
 * `address` is free text, so it may ALREADY end in the postal code and city. In
 * that case the helper's output is used as-is rather than appended to, which is
 * what `getUnitAddressSubtitle` would otherwise produce ("… 8008 Zürich, 8008
 * Zürich"). Comparison is accent- and case-insensitive on the trailing segment
 * only — a street genuinely called "Zürichstrasse" does not trip it, because the
 * check runs against the end of the string, not against any occurrence.
 */
function rankingUnitLocation(row) {
  const street = String(row?.address ?? "").trim();
  const tail = [String(row?.postal_code ?? "").trim(), String(row?.city ?? "").trim()]
    .filter(Boolean)
    .join(" ");

  if (street && tail && endsWithPlace(street, tail)) return street;

  const subtitle = getUnitAddressSubtitle(toUnitShape(row));
  return subtitle && subtitle !== "\u2014" ? subtitle : null;
}

const normalizePlace = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s,.]+/g, " ")
    .trim();

/** True when `street` already ends in `tail` ("… 8008 Zürich"), ignoring case, accents and separators. */
function endsWithPlace(street, tail) {
  const a = normalizePlace(street);
  const b = normalizePlace(tail);
  if (!a || !b) return false;
  return a === b || a.endsWith(` ${b}`);
}
